// Utils
import { unixToDate, tsToFormat } from 'libs/utils/time';
import { getSearcheable } from 'libs/utils/pax';
import { DEFAULT_DATE_FORMAT } from 'libs/utils/constants';

// Constants
import * as COLORS from 'libs/utils/global-colors';

/**
 * @typedef {Object} TimeSlots
 * @property {String} start_time
 * @property {String} end_time
 *
 * @typedef {Object} ValiditySlot
 * @property {String} start_date
 * @property {String} end_date
 * @property {TimeSlots[]} time_slots
 *
 * @example
 * [
 *      {
 *          "start_date": "2024-06-01",
 *          "end_date": "2024-06-16",
 *          "time_slots": [
 *              {
 *                  "start_time": "07:00",
 *                  "end_time": "12:30"
 *              },
 *              {
 *                  "start_time": "14:00",
 *                  "end_time": "20:00"
 *              }
 *          ]
 *      },
 *      {
 *          "start_date": "2024-06-17",
 *          "end_date": "2024-06-30",
 *          "time_slots": [
 *              {
 *                  "start_time": "00:00",
 *                  "end_time": "00:00"
 *              }
 *          ]
 *      }
 *  ]
 */

/**
 * @typedef {Object} InvaliditySlot see https://mobiscroll.com/docs/javascript/eventcalendar/api#opt-invalid
 * @property {string} start YYYY-MM-DD HH:mm
 * @property {string} end YYYY-MM-DD HH:mm
 * @property {string} resource _id of meeting_location
 */

/**
 * get "negative" of open slots between 00:00 and 23:59 on passed day
 * @param {string} resource
 * @param {string} day YYYY-MM-DD
 * @param {TimeSlots[]} openSlots
 * @returns {InvaliditySlot[]}
 */
export function getClosedSlotsFromOpen(resource, day, openSlots) {
    // sort slots by start_time
    const slots = openSlots.toSorted((a, b) => a.start_time > b.start_time ? 1 : -1);
    /** @type {[start:string,end:string][]} */
    const closeSlots = [];
    let prev = '00:00';
    for (const slot of slots) {
        // handle opening starting at 00:00
        if (slot.start_time === '00:00') {
            prev = slot.end_time;
            continue;
        }
        closeSlots.push([prev, slot.start_time]);
        prev = slot.end_time;
    }
    // handle opening not ending at 23:59
    if (prev !== '23:59') {
        closeSlots.push([prev, '23:59']);
    }
    return closeSlots.map(([start, end]) => ({
        start: `${day} ${start}`,
        end: `${day} ${end}`,
        resource,
    }));
}

/**
 * in order to be able to book a meeting of, for example, 1 hour at 23:00,
 * the end_time is set to 00:00 instead of 23:59 when creating the time slots for a room [here](https://github.com/Spotme/backstage-app/blob/3690c249a869862e968fae075b08a7ffe2e90a80/apps/backstage/src/ui/components/meetings/locations/LocationTimeSlots.vue#L133).
 * See [discussion](https://github.com/Spotme/backstage-app/blob/3690c249a869862e968fae075b08a7ffe2e90a80/apps/backstage/src/ui/components/meetings/locations/LocationTimeSlots.vue#L133) when it was implemented.
 * As 00:00 is effectively the beginning of a day, we switch it back to 23:59 here to signify that we mean the end of the day
 * @type {(timeSlots: TimeSlots) => TimeSlots}
 */
const fixEndTimeOfEndOfDay = ({ start_time, end_time }) =>
    ({
        start_time,
        end_time: end_time === '00:00' ? '23:59' : end_time,
    });

/**
 * @param {object} args
 * @param {string} args.eventTimezone
 * @param {number} args.currentDateTs unix timestamp
 * @param {{ _id: string, fp_type: string, validity_slots?: ValiditySlot[] }} args.location
 * @returns {InvaliditySlot[]}
 */
export function getInvaliditySlotsForDay({ eventTimezone, currentDateTs, location }) {
    if (location?.fp_type !== 'meeting_location' || !location?.validity_slots) return [];

    // day as YYYY-MM-DD in event timezone
    const day = tsToFormat(currentDateTs, DEFAULT_DATE_FORMAT, eventTimezone);

    // get open slots for the day
    /** @type {TimeSlots[]} */
    const slotsForDay = [];
    for (const slot of location.validity_slots) {
        if (slot.start_date <= day && slot.end_date >= day) {
            for (const d of slot.time_slots) {
                slotsForDay.push(fixEndTimeOfEndOfDay(d));
            }
        }
    }

    if (slotsForDay.length === 0) {
        // is invalid the whole day
        return [{
            start: `${day} 00:00`,
            end: `${day} 23:59`,
            resource: location._id,
        }];
    }

    if (
        slotsForDay.length === 1
        && slotsForDay[0].start_time === '00:00'
        && slotsForDay[0].end_time === '23:59'
    ) {
        // is valid the whole day
        return [];
    }

    // return "negative" of slots for day
    return getClosedSlotsFromOpen(location._id, day, slotsForDay);
}

/**
 * Computes the status of a meeting or a session.
 *
 * @param {Meeting|Session} event the meeting or session to compute the status from.
 * @param {object} resource the resource to compute the status for.
 * @param {string} resource.type the type of the resource.
 * @param {object} resource.acceptance the type of the resource.
 *
 * @returns {string} the meeting or session computed status.
 */
export function computeEventStatus(event, resource) {
    if (!event.acceptance) {
        if (resource.is_agenda) {
            return 'agenda';
        }
        return '';
    }

    const organizerAcceptance = event.acceptance[event.organizer._id];
    const organizerAccepted = organizerAcceptance === 'yes';
    const organizerDeclined = organizerAcceptance === 'no';
    const somePaxAccepted = event.participants.some(p => event.acceptance[p._id] === 'yes');
    const everyPaxDeclined = event.participants.length && event.participants.every(p => event.acceptance[p._id] === 'no');
    const hasExternalPax = event.externals?.length > 0;

    /**
     * Statuses definitions as per https://spotme.atlassian.net/browse/PB-3211:
     *
     * Confirmed → Meeting organizer has accepted + at least 1 invitee accepted
     * Declined → Meeting got declined by the organizer, or by all invitees
     * Invited → all the rest
     */
    if (organizerAccepted && (somePaxAccepted || hasExternalPax)) {
        return 'yes';
    } else if (organizerDeclined || (everyPaxDeclined && !hasExternalPax)) {
        return 'no';
    }
    return 'maybe';
}

/**
 * Reconciles the meeting organizer and participants with the provided participants array,
 * transforming the string IDs into the actual person objects.
 *
 * @param {Meeting[] & LoadedMeeting[]} meetings - The array of meetings object to reconcile.
 * @param {Person[]} participants - The array of participants to reconcile with.
 */
export function reconcileParticipants(meetings, participants) {
    for (const meeting of meetings) {
        if (typeof meeting.organizer === 'string') {
            meeting.organizer = participants.find(p => p._id === meeting.organizer);

            if (!meeting.organizer) {
                console.error('[utils/calendar] Organizer not found', meeting);
                meeting.organizer = {};
            }
        }

        if (meeting.participants.some(p => typeof p === 'string')) {
            const participantsDetails = participants.filter(p => meeting.participants.includes(p._id) && p._id !== meeting.organizer._id);

            meeting.participants = [
                ...participantsDetails,
                ...meeting.participants.slice(participantsDetails.length).map(pid => ({ _id: pid })),
            ];
        }
    }
}

/**
 * Converts a meeting object to a calendar event object.
 *
 * @param {Meeting} meeting - The meeting object.
 *
 * @returns {Object} The calendar event object.
 */
export function meetingToCalendarEvent(meeting = []) {
    try {
        const resources = [meeting.organizer._id, ...meeting.participants.map(p => p._id), meeting.meeting_location];

        // See
        // - https://mobiscroll.com/docs/javascript/eventcalendar/calendar#opt-data
        // - https://mobiscroll.com/docs/vue/eventcalendar/data-binding
        return {
            id: meeting._id,
            title: meeting.name,
            color: COLORS.COLOR_LIGHT_PRIMARY_300,
            resource: resources,
            start: unixToDate(meeting.start).toDate(),
            end: unixToDate(meeting.end).toDate(),
            origin: meeting,
            __search: getMeetingSearcheable(meeting)
        };
    } catch (error) {
        console.error('Error converting meeting to calendar event', error, meeting);
    }
}

/**
 * Converts a session object to a calendar event object.
 *
 * @param {Object} session - The session object to convert.
 *
 * @returns {Object} The calendar event object.
 */
export function sessionToCalendarEvent(session) {
    return {
        id: session._id,
        title: session.name,
        color: COLORS.COLOR_LIGHT_PRIMARY_300,
        resource: 'sessions',
        start: unixToDate(session.start),
        end: unixToDate(session.end),
        origin: session,
        is_session: true,
        __search: getMeetingSearcheable(session)
    };
}

/**
 * Returns a searchable string representation of a meeting.
 *
 * @param {Meeting} meeting - The meeting object.
 *
 * @returns {string} - The searchable string representation of the meeting.
 */
function getMeetingSearcheable(meeting) {
    const attendees = [meeting.organizer, ...(meeting.participants || [])];
    const attendeesSearch = attendees.map(a => getSearcheable(a)).join(' ').trim();
    const meetingSearch = meeting.name?.toLowerCase().trim() || '';
    return [meetingSearch, attendeesSearch].join(' ');
}

/**
 * Converts a pax or location object to a calendar resource object.
 *
 * @param {Person|Object} paxOrLocation - The pax or location object to convert.
 * @param {string} [color=COLORS.COLOR_LIGHT_PRIMARY_300] - The color of the calendar resource.
 *
 * @returns {Object} The converted calendar resource object.
 */
export function paxOrLocationToCalendarResource(paxOrLocation, color = COLORS.COLOR_LIGHT_PRIMARY_300) {
    const res = {
        id: paxOrLocation._id,
        color,
        origin: paxOrLocation,
        title: paxOrLocation.title || paxOrLocation.label,
        type: paxOrLocation.fp_type,
        is_agenda: false,
        __search: getSearcheable(paxOrLocation)
    };

    if (paxOrLocation.fp_type === 'person') {
        res.is_person = true;
    }

    if (paxOrLocation.fp_type === 'prospect') {
        res.is_prospect = true;
    }

    return res;
}
